import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface Channel { readonly channelId: string; readonly channelName: string; readonly channelType: string; }
interface Message { readonly messageId: string; readonly body: string; readonly senderId: string; readonly createdAt: string; readonly attachmentType?: string; }

/** M27 — Messaging Center (§31). Channel directory + message stream of the
 * selected channel, with a compose box that posts to the messaging API. */
export function M27MessagingCenterPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [channels, setChannels] = useState<Channel[]>([]);
  const [selected, setSelected] = useState<string>('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: Channel[] }>('/v1/collab/channels', { token })
      .then((r) => { if (alive) { setChannels(r.data); if (r.data[0]) setSelected(r.data[0].channelId); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  useEffect(() => {
    if (!selected) return;
    let alive = true;
    apiRequest<{ data: Message[] }>(`/v1/collab/messages?channelId=${encodeURIComponent(selected)}`, { token })
      .then((r) => { if (alive) { setMessages(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token, selected]);

  async function send(): Promise<void> {
    if (!draft.trim() || !selected) return;
    try {
      const r = await apiRequest<{ data: Message }>('/v1/collab/messages', { token, method: 'POST', body: { channelId: selected, body: draft } });
      setMessages((m) => [...m, r.data]);
      setDraft('');
    } catch (e) { setError((e as Error).message); }
  }

  async function markRead(id: string): Promise<void> {
    setBusy(true); setNotice(null);
    try {
      await apiRequest(`/v1/collab/messages/${id}/read`, { token, method: 'POST' });
      setNotice(t('m27.messaging.noticeMarkedRead'));
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  async function react(id: string): Promise<void> {
    const emoji = window.prompt(t('m27.messaging.promptReaction'));
    if (emoji === null || !emoji.trim()) return;
    setBusy(true); setNotice(null);
    try {
      await apiRequest(`/v1/collab/messages/${id}/react`, { token, method: 'POST', body: { emoji } });
      setNotice(t('m27.messaging.noticeReactionAdded'));
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  async function remove(id: string): Promise<void> {
    setBusy(true); setNotice(null);
    try {
      await apiRequest(`/v1/collab/messages/${id}`, { token, method: 'DELETE' });
      setMessages((m) => m.filter((x) => x.messageId !== id));
      setNotice(t('m27.messaging.noticeDeleted'));
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <main aria-label="messaging-center">
      <h1>{t('m27.messaging.heading')}</h1>
      {notice && <p role="status">{notice}</p>}
      {error && <p role="alert">{error}</p>}
      <label htmlFor="channel-select">{t('m27.messaging.fldChannel')}</label>
      <select id="channel-select" value={selected} onChange={(e) => setSelected(e.target.value)}>
        {channels.map((c) => <option key={c.channelId} value={c.channelId}>{c.channelName}</option>)}
      </select>
      {loading ? <p>{t('m27.messaging.loading')}</p> : (
        <>
          <p aria-label="message-count">{t('m27.messaging.count', { count: messages.length })}</p>
          <ul aria-label="message-list">{messages.map((m) => (
            <li key={m.messageId}>
              {m.body}
              {m.attachmentType && m.attachmentType !== 'TEXT' && <span aria-label="attachment-type"> [{m.attachmentType}]</span>}
              <button type="button" aria-label={`read-${m.messageId}`} disabled={busy} onClick={() => void markRead(m.messageId)}>{t('m27.messaging.btnRead')}</button>
              <button type="button" aria-label={`react-${m.messageId}`} disabled={busy} onClick={() => void react(m.messageId)}>{t('m27.messaging.btnReact')}</button>
              <button type="button" aria-label={`delete-${m.messageId}`} disabled={busy} onClick={() => void remove(m.messageId)}>{t('m27.messaging.btnDelete')}</button>
            </li>
          ))}</ul>
          <label htmlFor="compose">{t('m27.messaging.fldMessage')}</label>
          <textarea id="compose" value={draft} onChange={(e) => setDraft(e.target.value)} />
          <button type="button" onClick={() => void send()}>{t('m27.messaging.btnSend')}</button>
        </>
      )}
    </main>
  );
}
